import { useState } from 'react';
import { UserButton, useUser } from '@clerk/clerk-react';
import { Briefcase, RefreshCw } from 'lucide-react';
import { useJobs } from '@/hooks/useJobs';
import { useAssessmentSummaries } from '@/hooks/useAssessmentSummaries';
import { JobList } from '@/components/JobList';
import { JobDetail } from '@/components/JobDetail';
import { Button } from '@/components/ui/button';
import type { SavedJob } from '@/lib/types';

export function Dashboard() {
  const { user } = useUser();
  const { jobs, loading, error, refresh, remove, toggleStar } = useJobs();
  const { byJob: summaries, refresh: refreshSummaries } = useAssessmentSummaries();
  const [selected, setSelected] = useState<SavedJob | null>(null);

  async function handleRefreshAll() {
    await Promise.all([refresh(), refreshSummaries()]);
  }

  async function handleDelete(id: string) {
    try {
      await remove(id);
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleToggleStar(job: SavedJob) {
    try {
      await toggleStar(job);
      if (selected?.id === job.id) setSelected({ ...job, starred: !job.starred });
    } catch (err) {
      console.error(err);
    }
  }

  const total = jobs.length;
  const starredCount = jobs.filter((j) => j.starred).length;

  if (selected) {
    return (
      <JobDetail
        job={selected}
        summary={summaries[selected.id]}
        onBack={() => {
          setSelected(null);
          // The detail view may have generated a quiz / submitted an assessment;
          // re-sync so the list badges reflect it.
          void refreshSummaries();
        }}
        onDelete={handleDelete}
        onToggleStar={handleToggleStar}
        onAssessmentChange={() => void refreshSummaries()}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-12 pt-4 sm:px-6">
      <header className="sticky top-0 z-10 -mx-4 mb-4 flex items-center justify-between border-b border-border bg-card/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Briefcase className="h-4 w-4" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold leading-tight tracking-tight">
              JobFit
            </h1>
            <p className="truncate text-xs text-muted-foreground">
              {user?.primaryEmailAddress?.emailAddress ?? user?.username ?? 'Signed in'}
              {total > 0 ? (
                <span className="ml-2 text-muted-foreground">
                  · {starredCount} starred · {total} saved
                </span>
              ) : null}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void handleRefreshAll()}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
          <UserButton afterSignOutUrl="/" />
        </div>
      </header>

      <JobList
        jobs={jobs}
        summaries={summaries}
        loading={loading}
        error={error}
        onSelect={setSelected}
        onDelete={handleDelete}
        onToggleStar={handleToggleStar}
        onRefresh={() => void handleRefreshAll()}
      />
    </div>
  );
}